/**
 * Tracking-number formatting shared by every surface that lets an operator
 * enter or follow one.
 *
 * Lifted out of Collections.jsx when the No Tracking exception lane gained the
 * same paste-a-URL input: an operator copies a carrier's tracking page out of
 * an email, and both places must reduce it to the same bare number, or the same
 * shipment gets stored two different ways depending on which screen was open.
 */

/** The carrier's public tracking page for a number, or null when unmappable. */
export function getTrackingUrl(trackingNumber, carrier) {
    if (!trackingNumber || !carrier) return null
    const carrierLower = carrier.toLowerCase()

    if (carrierLower.includes('royal')) {
        return `https://www.royalmail.com/track-your-item#/tracking-results/${trackingNumber}`
    }
    if (carrierLower.includes('parcel')) {
        if (trackingNumber.toUpperCase().startsWith('PFW')) {
            return `https://send.parcelforce.com/tracking/${trackingNumber.toLowerCase()}`
        }
        return `https://www.royalmail.com/track-your-item#/tracking-results/${trackingNumber}`
    }
    if (carrierLower.includes('fedex')) {
        return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
    }
    if (carrierLower.includes('dhl')) {
        return `https://www.dhl.com/gb-en/home/tracking.html?tracking-id=${trackingNumber}`
    }
    return null
}

/** Extract just the tracking number from a pasted URL or free text. */
export function extractTrackingNumber(input) {
    if (!input) return ''
    // If it's already just a number/code, return it
    if (!input.includes('http') && !input.includes('/')) {
        return input.trim().toUpperCase()
    }
    // Try to extract from common tracking URLs
    const patterns = [
        /tracking-results\/([A-Z0-9]+)/i,
        /tracking\/([A-Z0-9]+)/i,
        /trknbr=([A-Z0-9]+)/i,
        /tracking-id=([A-Z0-9]+)/i,
        /([A-Z]{2}\d{9}GB)/i, // Royal Mail format
        /(PF[A-Z]?\d+)/i, // ParcelForce format
        /(\d{12,15})/  // FedEx format
    ]
    for (const pattern of patterns) {
        const match = input.match(pattern)
        if (match) return match[1].toUpperCase()
    }
    return input.trim().toUpperCase()
}
